/* eslint-disable react/no-array-index-key */
import {
  ChangeEvent,
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { IoIosInformationCircle } from 'react-icons/io';
import { FaCheck, FaEye, FaEyeSlash } from 'react-icons/fa';

import { TColorProp, useTheme } from 'styled-components';

import { unmask } from '~/common/utils/unmask';
import { inter } from '~/common/styles/fonts';
import { Loader } from '~/common/components/Loader';

import { ITextInputProps } from './types';
import {
  Container,
  InputWrapper,
  CustomInput,
  CounterWrapper,
  PrefixOrSuffixWrapper,
  CopySuffixWrapper,
  getTextInputHeight,
  LoaderWrapper,
  GlowWrapper,
  GlowEffect,
} from './styles';
import { Typography } from '../Typograph';
import { HelperText } from '../HelperText';
import { FormLabel } from '../FormLabel';

const BaseTextInput: ForwardRefRenderFunction<
  HTMLInputElement,
  ITextInputProps
> = (
  {
    style,
    label,
    size = 'medium',
    width = '300px',
    error,
    isDisabled,
    helperText,
    leftCustomIcon,
    rightCustomIcon,
    withBorderEffect = false,
    textAreaDynamicFullHeight = false,
    leftIcon: LeftIcon = null,
    rightIcon: RightIcon = null,
    as = 'input',
    maxLength,
    _prefix,
    withoutPrefixDelimiter = false,
    _suffix,
    centralize = false,
    withCopySuffix = false,
    withoutBorder = false,
    loading = false,
    loader = 'clip',
    mask,
    onChange,
    backgroundColor = 'neutral0',
    borderColor = 'neutral950',
    onHoverBackgroundColor = 'neutral0',
    onFocusBorderColor = 'neutral950',
    ...props
  },
  ref
) => {
  const id = useId();
  const theme = useTheme();
  const [currentCount, setCurrentCount] = useState(0);
  const [focus, setFocus] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Expõe o ref interno para o pai, se a ref externa for passada
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const inputType =
    props?.type === 'password'
      ? showPassword
        ? 'text'
        : 'password'
      : props.type;

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;

    if (maxLength > 0 && as === 'textarea') {
      const currentLength = value.length;
      if (currentLength > maxLength) {
        return;
      }

      setCurrentCount(currentLength);
    }

    if (mask) {
      Object.assign(event.target, {
        value: unmask(event.target.value),
      });
    }

    onChange?.(event);
  };

  const $hasPrefixOrSuffix = useMemo(
    () => !!_prefix || !!_suffix || withCopySuffix,
    [_prefix, _suffix, withCopySuffix]
  );

  const getTextAreaHeight = useCallback(() => {
    if (as === 'input') {
      return null;
    }

    if (!style?.minHeight) {
      return null;
    }

    if (label) {
      return `calc(${style.minHeight} - 24px)`;
    }

    return String(style.minHeight);
  }, [as, label, style]);

  const InputElement = (
    <InputWrapper
      $hasError={!!error}
      $isDisabled={isDisabled}
      $size={size}
      $width={width}
      $hasPrefixOrSuffix={$hasPrefixOrSuffix}
      $isPassword={inputType === 'password' || showPassword}
      $withoutBorder={withoutBorder}
      $inputAs={as}
      $textAreaHeight={getTextAreaHeight()}
      $backgroundColor={backgroundColor}
      $borderColor={borderColor}
      $onFocusBorderColor={onFocusBorderColor}
      $onHoverBackgroundColor={onHoverBackgroundColor}
    >
      {_prefix && (
        <PrefixOrSuffixWrapper
          $focus={focus}
          $size={size}
          $borderPosition="right"
          $borderColor={borderColor as TColorProp}
          $withoutPrefixDelimiter={withoutPrefixDelimiter}
        >
          {_prefix}
        </PrefixOrSuffixWrapper>
      )}

      {leftCustomIcon ? (
        <>{leftCustomIcon}</>
      ) : LeftIcon ? (
        <LeftIcon
          id="input-icon-id"
          size={20}
          color={isDisabled ? theme.neutral600 : theme.neutral500}
        />
      ) : null}

      <CustomInput
        textAreaHeight={getTextAreaHeight()}
        centralize={centralize}
        as={as}
        disabled={isDisabled}
        id={id}
        ref={inputRef}
        maxLength={maxLength}
        {...props}
        type={inputType}
        className={inter.className}
        onChange={handleChange}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        {...(props.value &&
          mask && {
            value: mask(props.value as string),
          })}
        {...($hasPrefixOrSuffix &&
          !!LeftIcon && {
            style: {
              paddingLeft: '0 !important',
            },
          })}
        {...($hasPrefixOrSuffix &&
          !!RightIcon && {
            style: {
              paddingRight: '0 !important',
            },
          })}
      />

      {rightCustomIcon ? (
        <>{rightCustomIcon}</>
      ) : RightIcon ? (
        <RightIcon
          id="input-icon-id"
          size={20}
          color={isDisabled ? theme.neutral600 : theme.neutral500}
        />
      ) : null}

      {loading && (
        <LoaderWrapper>
          <Loader
            size={Number(getTextInputHeight[size].replace('px', ''))}
            type={loader}
            color="neutral50"
          />
        </LoaderWrapper>
      )}

      {props?.type === 'password' && (
        <CopySuffixWrapper
          $focus={focus}
          $size={size}
          onClick={() => setShowPassword((prev) => !prev)}
          style={{ cursor: 'pointer' }}
        >
          {showPassword ? (
            <FaEye color={theme.neutral500} size={20} />
          ) : (
            <FaEyeSlash color={theme.neutral500} size={20} />
          )}
        </CopySuffixWrapper>
      )}

      {maxLength && as === 'textarea' && (
        <CounterWrapper $size={size}>
          <Typography.Subheading size="2x-small" color="neutral500">
            {currentCount}/{maxLength}
          </Typography.Subheading>
        </CounterWrapper>
      )}

      {_suffix && (
        <PrefixOrSuffixWrapper
          $focus={focus}
          $size={size}
          $borderPosition="left"
          $borderColor={borderColor}
          $withoutPrefixDelimiter={withoutPrefixDelimiter}
        >
          {_suffix}
        </PrefixOrSuffixWrapper>
      )}
    </InputWrapper>
  );

  return (
    <Container
      htmlFor={id}
      style={style}
      textAreaDynamicFullHeight={textAreaDynamicFullHeight}
    >
      {!!label && <FormLabel {...label} isDisabled={isDisabled} />}
      {withBorderEffect ? (
        <GlowWrapper>
          <GlowEffect />
          {InputElement}
        </GlowWrapper>
      ) : (
        <>{InputElement}</>
      )}

      {(!!helperText || !!error) && (
        <>
          {Array.isArray(helperText) ? (
            <>
              {helperText.map(({ text, check }, index) => (
                <HelperText
                  key={String(index)}
                  isDisabled={isDisabled}
                  isError={!!error}
                  icon={check ? FaCheck : IoIosInformationCircle}
                  iconColor={check ? 'primary' : 'neutral500'}
                >
                  {text}
                </HelperText>
              ))}
            </>
          ) : (
            <HelperText isDisabled={isDisabled} isError={!!error}>
              {error || helperText}
            </HelperText>
          )}
        </>
      )}
    </Container>
  );
};

export const TextInput = forwardRef(BaseTextInput);
